// @flow
import OrgnavRouter from 'utils/OrgnavRouter'
import {offset} from '@floating-ui/dom'
import hotwire from '../../hotwire'

export const tourOptions = {
  defaultStepOptions: {
    classes: 'shepherd-theme-custom',
    cancelIcon: {
      enabled: true,
    },
  },
  useModalOverlay: true,
}

const buttonConfig = (tour) => ([
  {
    classes: 'shepherd-button-secondary',
    text: 'Back',
    action: () => tour.back(),
  },
  {
    classes: 'shepherd-button-primary',
    text: 'Continue',
    action: () => {
      tour.next()
    },
  },
])

type Props = {
  tour: Object,
  history: Object,
  roleDatabaseId: string,
}

const tooltip = (linkText, tipKey) => `<span data-bs-container=".shepherd-element:visible" data-bs-content='${I18n.t(`definitions.${tipKey}`)}' data-bs-toggle="popover" tabindex="0">${linkText}</span>`

export const getSteps = ({tour, history, roleDatabaseId}: Props): Object => ([
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: ''}))
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    buttons: buttonConfig(tour),
    title: 'Guide to Circle and Role Pages',
    text: [`<div><p>This is a ${tooltip('circle', 'circle')} or role page. </p>
       <p>All information regarding the ${tooltip('circle', 'circle')}
       or role can be found here.</p></div>`],
  },
  {
    when: {
      show() {
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#sjs-circle_name > div',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>This is the name of the ${tooltip('circle', 'circle')} or
        role.</p></div>`],
  },
  {
    when: {
      show() {
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#sjs-follow_button',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    classes: 'headerless',
    buttons: buttonConfig(tour),
    text: [`<div><p>Here you can follow or unfollow the 
        ${tooltip('circle', 'circle')}/role.
        Following will ensure you get emails regarding any updates that occur.</p>
        <p>You automatically follow ${tooltip('circles', 'circle')} and
        roles you are a member of.</p></div>`],
  },
  {
    when: {
      show() {
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#sjs-tags_button',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    buttons: buttonConfig(tour),
    modalOverlayOpeningPadding: 6,
    classes: 'headerless',
    text: [`<div><p>Here you can hide and show the
        ${tooltip('circle', 'circle')} or role's tags.
        Tags can be really handy in searches.</p></div>`],
  },
  {
    when: {
      show() {
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#sjs-threeDot',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>This menu allows you to manage various settings for the
        ${tooltip('circle', 'circle')}/role and offers features such as printing
        its details or ${tooltip('governance', 'governance')}.</p></div>`],
  },
  {
    when: {
      show() {
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#sjs-meeting_trigger',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>From here you can start a meeting for the
        ${tooltip('circle', 'circle')}.</p></div>`],
  },
  {
    when: {
      show() {
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '.chart-container svg',
      on: 'left',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>This section allows you to navigate your organization. You can click on
     ${tooltip('circles', 'circle')} and roles in the diagram or on the names below to
     their page.</p></div>`],
  },
  {
    when: {
      show() {
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#sjs-role_browser',
      on: 'left',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>This area shows all of the
      ${tooltip('circles', 'circle')} and roles that make up the
      ${tooltip('circle', 'circle')} you are viewing.</p></div>`],
  },
  {
    when: {
      show() {
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#sjs-tab_navigation',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>Here you can navigate within the
      ${tooltip('circle', 'circle')} or role.</p></div>`],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'overview'}))
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#Overview-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>The default tab is called 'Overview', and it shows general information 
      like who fills it and its ${tooltip('Purpose', 'purpose')}.</p><div>`],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'roles'}))
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#Roles-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>The 'Roles' tab only shows up if you're looking at a
      ${tooltip('circle', 'circle')}.</p>
      <p>The 'Roles' tab lists all roles within the
      ${tooltip('circle', 'circle')}, and is also where they are managed.</p></div>`],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'members'}))
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#Members-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>In 'Members' you will find a list of all
      ${tooltip('members', 'member')} of the
      ${tooltip('circle', 'circle')} as well as a link to email them all.</p></div>`],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'policies'}))
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#Policies-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>'Policies' lists all
      ${tooltip('policies', 'policies')} that define the authority and special rules
      of the ${tooltip('circle', 'circle')}.</p></div>`],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'notes'}))
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#Notes-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>And 'Notes' lists all ${tooltip('notes', 'notes')}
        so you can better keep track and share useful information.</p></div>`],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'projects'}))
        hotwire.enableBsPopovers('.shepherd-content')
      },
    },
    attachTo: {
      element: '#Projects-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>'Projects' offers a filterable list of all the
      ${tooltip('project', 'projects')}s associated to the
      ${tooltip('circle', 'circle')}.</p></div>`],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'checklists'}))
      },
    },
    attachTo: {
      element: '#Checklists-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: ['<div><p>Checklists offer a place to track recurring actions, such as \'payroll processed\'.</p></div>'],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'metrics'}))
      },
    },
    attachTo: {
      element: '#Metrics-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: ['<div><p>Metrics can capture data and reporting to track and share, like \'monthly revenue\'.</p></div>'],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'goals'}))
      },
    },
    attachTo: {
      element: '#Goals-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: [`<div><p>If you organization is using the Goals & Targets feature in GlassFrog you'll be able to see
      them in the 'Goals' tab.</p></div>`],
  },
  {
    when: {
      show() {
        history.push(OrgnavRouter.rolePath({roleDatabaseId, tabName: 'history'}))
      },
    },
    attachTo: {
      element: '#History-tab',
      on: 'bottom',
    },
    floatingUIOptions: {
      middleware: [offset({mainAxis: 12, crossAxis: 0})],
    },
    modalOverlayOpeningPadding: 6,
    buttons: buttonConfig(tour),
    classes: 'headerless',
    text: ['<div><p>And finally, the \'History\' tab lists all meetings and meeting outputs.</p></div>'],
  },
])
